import React, { useEffect, useState } from "react";
import { Box, Typography, Backdrop } from "@mui/material";
import { waveAnimation, dotAnimation } from "../../styles/aiAnimations";
import { aiBackdropStyles, aiLoaderCardStyles, aiIconStyles } from "../../styles/aiStyles";
import { AI_COLORS } from "../../styles/aiTheme";

interface AIAudioLoaderProps {
    visible: boolean;
}

const AIAudioLoader: React.FC<AIAudioLoaderProps> = ({ visible }) => {
    const [currentStep, setCurrentStep] = useState(0);

    const steps = [
        "Processing audio...",
        "Analyzing speech patterns...",
        "Generating transcription...",
        "Creating AI disposition...",
        "Finalizing results..."
    ];

    useEffect(() => {
        if (!visible) {
            setCurrentStep(0);
            return;
        }

        const interval = setInterval(() => {
            setCurrentStep((prev) => (prev + 1) % steps.length);
        }, 2000);

        return () => clearInterval(interval);
    }, [visible, steps.length]);

    if (!visible) return null;

    return (
        <Backdrop
            sx={aiBackdropStyles}
            open={visible}
        >
            <Box sx={aiLoaderCardStyles}>
                {/* AI Brain Icon */}
                <Box sx={aiIconStyles}>
                    <Typography
                        sx={{
                            fontSize: '2rem',
                            color: 'white',
                            fontWeight: 'bold'
                        }}
                    >
                        🤖
                    </Typography>
                </Box>

                {/* Audio Wave Animation */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-end',
                        gap: '4px',
                        height: '80px'
                    }}
                >
                    {[...Array(7)].map((_, index) => (
                        <Box
                            key={index}
                            sx={{
                                width: '8px',
                                backgroundColor: AI_COLORS.primary,
                                borderRadius: '4px',
                                animation: `${waveAnimation} 1.5s infinite ease-in-out`,
                                animationDelay: `${index * 0.1}s`
                            }}
                        />
                    ))}
                </Box>

                {/* Step Text */}
                <Typography
                    variant="h6"
                    sx={{
                        color: AI_COLORS.text.primary,
                        fontWeight: 600,
                        fontSize: '1.1rem'
                    }}
                >
                    {steps[currentStep]}
                </Typography>

                {/* Animated Dots */}
                <Box
                    sx={{
                        display: 'flex',
                        gap: '8px'
                    }}
                >
                    {[0, 1, 2].map((index) => (
                        <Box
                            key={index}
                            sx={{
                                width: '12px',
                                height: '12px',
                                borderRadius: '50%',
                                backgroundColor: AI_COLORS.primary,
                                animation: `${dotAnimation} 1.4s infinite ease-in-out both`,
                                animationDelay: `${index * 0.16}s`
                            }}
                        />
                    ))}
                </Box>

                {/* Subtitle */}
                <Typography
                    variant="body2"
                    sx={{
                        color: AI_COLORS.text.tertiary,
                        fontSize: '0.9rem',
                        maxWidth: '280px'
                    }}
                >
                    Our AI is analyzing your audio with collectedge Intelligence to provide intelligent disposition suggestions
                </Typography>
            </Box>
        </Backdrop>
    );
};

export default AIAudioLoader;
